import { BaseScanGridWidget } from './BaseScanGridWidget';
import { evaluateDataSourceConfig } from './KeyFindingsAdvancedWidgetHelper';
import { evaluateThreshold } from '../../utils/thresholds';

/**
 * Cyber Security (Threat & Exposure) - Scan Definitions
 */
export const DEFAULT_CYBER_SECURITY_CATEGORIES = [
  { id: 'cyber-1', label: 'External Attack Surface Exposure' },
  { id: 'cyber-2', label: 'Privileged Accounts Without MFA' },
  { id: 'cyber-3', label: 'Legacy Authentication Protocols' },
  { id: 'cyber-4', label: 'Known Vulnerable Software (CVE-Based)' },
  { id: 'cyber-5', label: 'Lateral Movement Risk' },
  { id: 'cyber-6', label: 'Email Attack Exposure' },
  { id: 'cyber-7', label: 'Persistence Mechanisms' },
  { id: 'cyber-8', label: 'Weak Encryption & Protocol Usage' },
  { id: 'cyber-9', label: 'Administrative Access Sprawl' }
];

export const extractCyberSecurityFindings = (data, config = {}) => {
  // Handle different data structures robustly
  const scanData = data?.scanData || data?.data || data || {};
  
  // Ensure we have an object to work with
  const actualScanData = (typeof scanData === 'object' && !Array.isArray(scanData)) ? scanData : {};
  
  const localhost = actualScanData.localhost || actualScanData['Localhost Scan'] || actualScanData.System || {};
  const networkDevices = Array.isArray(actualScanData.network_devices) ? actualScanData.network_devices : [];
  const adData = actualScanData['Active Directory Scan'] || actualScanData['AD Scan'] || actualScanData['Ad Scan'] || {};
  const dataOverrides = config.dataOverrides || {};
  
  const findings = [];

  const createFinding = (id, title, status, score, description, value = null, metrics = []) => ({
    id, title, status, score, description, value, metrics, category: 'Threat & Exposure'
  });

  const evaluateStatusAndScore = (value, total, id, defaultMode = 'percentage') => {
    // 1. Check for dynamic category thresholds from backend
    const categoryName = DEFAULT_CYBER_SECURITY_CATEGORIES.find(c => c.id === id)?.label;
    const dynamicThresholds = actualScanData.category_thresholds?.[categoryName];

    if (dynamicThresholds && dynamicThresholds.rules) {
      const tier = evaluateThreshold(value, 'numeric', dynamicThresholds.rules);
      const statusMap = { 'Good': 'Green', 'Moderate': 'Yellow', 'Bad': 'Red' };
      const scoreMap = { 'Good': 100, 'Moderate': 50, 'Bad': 0 };
      
      if (tier && tier !== 'Unknown') {
        return { 
          status: statusMap[tier] || 'Unknown', 
          score: scoreMap[tier] || 0, 
          value, 
          mode: 'dynamic' 
        };
      }
    }

    // 2. Fallback to existing logic
    const override = dataOverrides[id] || {};
    const mode = override.scoringMode || defaultMode;
    const defaultGreen = mode === 'count_max' ? 0 : 90;
    const defaultYellow = mode === 'count_max' ? 1 : 70;
    
    const greenThreshold = override.greenThreshold !== undefined ? override.greenThreshold : defaultGreen;
    const yellowThreshold = override.yellowThreshold !== undefined ? override.yellowThreshold : defaultYellow;

    let score = 0; 
    let status = 'Unknown';

    if (mode === 'percentage') {
      const calcScore = total > 0 ? (value / total) * 100 : 0;
      score = Number.isInteger(calcScore) ? calcScore : parseFloat(calcScore.toFixed(2));
      if (score >= greenThreshold) status = 'Green';
      else if (score >= yellowThreshold) status = 'Yellow';
      else status = 'Red';
    } else if (mode === 'count_min') {
      if (value >= greenThreshold) status = 'Green';
      else if (value >= yellowThreshold) status = 'Yellow';
      else status = 'Red';
      
      if (status === 'Green') score = 100;
      else if (status === 'Yellow') score = 50;
      else score = 0;
    } else if (mode === 'count_max') {
      if (value <= greenThreshold) status = 'Green';
      else if (value <= yellowThreshold) status = 'Yellow';
      else status = 'Red';
      
      if (status === 'Green') score = 100;
      else if (status === 'Yellow') score = 50;
      else score = 0;
    }

    return { status, score, value, mode };
  };

  // --- 1. External Attack Surface Exposure ---
  // Logic: Scan network_devices for exposed critical ports
  const riskyPorts = [21, 23, 445, 1080, 1433, 1521, 3306, 3389, 5432, 5900, 5985, 5986];
  let exposedPortsCount = 0;
  networkDevices.forEach(dev => {
    if (dev.ports) {
      const openPorts = dev.ports.split(',').map(p => parseInt(p.trim())).filter(p => !isNaN(p));
      const foundRisky = openPorts.filter(p => riskyPorts.includes(p));
      exposedPortsCount += foundRisky.length;
    }
  });
  const exposedResult = evaluateStatusAndScore(exposedPortsCount, null, 'cyber-1', 'count_max');
  findings.push(createFinding('cyber-1', 'External Attack Surface Exposure', exposedResult.status, exposedResult.score, 
    exposedPortsCount === 0 ? 'No unnecessary services exposed to the internet' : `${exposedPortsCount} potentially risky services exposed to the external network`, 
    exposedPortsCount));

  // --- 2. Privileged Accounts Without MFA ---
  // Logic: Check AD data for privileged roles without MFA
  const adUsers = adData['Ad Users Audit'] || adData['Ad Users'] || [];
  const privilegedGroups = ['Domain Admins', 'Enterprise Admins', 'Schema Admins', 'Administrators'];
  const privWithoutMfa = adUsers.filter(u => {
    const isPriv = u.Groups?.some(g => privilegedGroups.some(pg => g.includes(pg)));
    return isPriv && u.MfaStatus !== 'Enabled' && u.MfaStatus !== 'Enforced';
  });
  const mfaResult = evaluateStatusAndScore(privWithoutMfa.length, adUsers.length, 'cyber-2', 'count_max');
  findings.push(createFinding('cyber-2', 'Privileged Accounts Without MFA', mfaResult.status, mfaResult.score, 
    privWithoutMfa.length === 0 ? 'All privileged accounts protected by MFA' : `${privWithoutMfa.length} privileged accounts lack multi-factor protection`, 
    privWithoutMfa.length));

  // --- 3. Legacy Authentication Protocols ---
  // Logic: Check for NTLMv1, SMBv1, Basic Auth
  const sysInfo = localhost['System Informations'] || [];
  const legacyProtocolsFound = sysInfo.filter(item => 
    (item.Property === 'SMBv1' && item.Value === 'Enabled') || 
    (item.Property === 'NTLMv1' && item.Value === 'Enabled')
  ).length;
  const legacyResult = evaluateStatusAndScore(legacyProtocolsFound, null, 'cyber-3', 'count_max');
  findings.push(createFinding('cyber-3', 'Legacy Authentication Protocols', legacyResult.status, legacyResult.score, 
    legacyProtocolsFound === 0 ? 'Legacy authentication fully disabled' : `${legacyProtocolsFound} legacy protocols or insecure auth methods detected`, 
    legacyProtocolsFound));

  // --- 4. Known Vulnerable Software (CVE-Based) ---
  // Logic: Match installed software against CVE metrics
  const installedApps = localhost['Installed Applications'] || [];
  const vulnerableApps = installedApps.filter(app => app.Risk === 'High' || app.Risk === 'Critical' || (app.CVE && app.CVE.length > 0));
  const vulnResult = evaluateStatusAndScore(vulnerableApps.length, installedApps.length, 'cyber-4', 'count_max');
  findings.push(createFinding('cyber-4', 'Known Vulnerable Software (CVE-Based)', vulnResult.status, vulnResult.score, 
    vulnerableApps.length === 0 ? 'No known high-risk vulnerabilities detected' : `${vulnerableApps.length} applications with known critical vulnerabilities identified`, 
    vulnerableApps.length));

  // --- 5. Lateral Movement Risk ---
  // Logic: High number of admins or credential persistence
  const localAdmins = localhost['Local Admins'] || [];
  const lateralMovementIssues = localAdmins.length > 5 ? 1 : 0; // Simplified threshold
  const lateralResult = evaluateStatusAndScore(lateralMovementIssues, null, 'cyber-5', 'count_max');
  findings.push(createFinding('cyber-5', 'Lateral Movement Risk', lateralResult.status, lateralResult.score, 
    lateralMovementIssues === 0 ? 'Lateral movement opportunities minimized' : 'Excessive local administrative rights increase lateral movement risk', 
    lateralMovementIssues));

  // --- 6. Email Attack Exposure ---
  // Logic: Forwarding rules, DMARC/SPF issues in MS365
  const ms365 = scanData.ms365 || scanData['Microsoft 365 Scan'] || {};
  const emailIssues = (ms365.ForwardingRules?.length || 0) + (ms365.DmarcIssues?.length || 0);
  const emailResult = evaluateStatusAndScore(emailIssues, null, 'cyber-6', 'count_max');
  findings.push(createFinding('cyber-6', 'Email Attack Exposure', emailResult.status, emailResult.score, 
    emailIssues === 0 ? 'Email security controls properly enforced' : `${emailIssues} email configuration issues increase phishing/spoofing risk`, 
    emailIssues));

  // --- 7. Persistence Mechanisms ---
  // Logic: Suspicious scheduled tasks or startup items
  const startupItems = localhost['Startup Items'] || [];
  const tasks = localhost['Scheduled Tasks'] || [];
  const persistenceIssues = startupItems.filter(i => i.Status === 'Suspicious').length + tasks.filter(t => t.Status === 'Suspicious').length;
  const persistResult = evaluateStatusAndScore(persistenceIssues, null, 'cyber-7', 'count_max');
  findings.push(createFinding('cyber-7', 'Persistence Mechanisms', persistResult.status, persistResult.score, 
    persistenceIssues === 0 ? 'No suspicious persistence mechanisms detected' : `${persistenceIssues} unauthorized or suspicious persistence entries detected`, 
    persistenceIssues));

  // --- 8. Weak Encryption & Protocol Usage ---
  // Logic: TLS 1.0/1.1 detected or weak ciphers
  const sslIssues = networkDevices.filter(dev => dev.protocols?.includes('TLS 1.0') || dev.protocols?.includes('TLS 1.1')).length;
  const encResult = evaluateStatusAndScore(sslIssues, null, 'cyber-8', 'count_max');
  findings.push(createFinding('cyber-8', 'Weak Encryption & Protocol Usage', encResult.status, encResult.score, 
    sslIssues === 0 ? 'Strong encryption standards enforced' : `${sslIssues} systems using deprecated or weak encryption protocols`, 
    sslIssues));

  // --- 9. Administrative Access Sprawl ---
  // Logic: Privileged group size checks
  const adGroups = adData['Ad Groups Audit'] || adData['Ad Groups'] || [];
  const domAdmins = adGroups.find(g => g.Name === 'Domain Admins');
  const sprawlCount = domAdmins ? (domAdmins.MembersCount || domAdmins.Members?.length || 0) : 0;
  const sprawlResult = evaluateStatusAndScore(sprawlCount, null, 'cyber-9', 'count_max');
  findings.push(createFinding('cyber-9', 'Administrative Access Sprawl', sprawlResult.status, sprawlResult.score, 
    sprawlCount < 10 ? 'Administrative access tightly scoped' : `High number of Domain Administrators (${sprawlCount}) indicates privilege sprawl`, 
    sprawlCount));

  return findings;
};

/**
 * Default Status descriptions map for Cyber Security Grid
 */
export const getCyberSecurityStatusDescription = (findingId, status) => {
    const descriptions = {
      'cyber-1': { 'Green': 'No unnecessary services exposed to the internet', 'Yellow': 'Limited exposure detected; non-critical services found', 'Red': 'Critical services exposed (e.g., RDP, SMB) to the web' },
      'cyber-2': { 'Green': 'All privileged accounts protected by MFA', 'Yellow': 'MFA exceptions found for some privileged accounts', 'Red': 'Privileged accounts without MFA detected' },
      'cyber-3': { 'Green': 'Legacy authentication fully disabled (SMBv1/NTLMv1)', 'Yellow': 'Legacy protocols detected but with limited scope', 'Red': 'Insecure legacy authentication active across the network' },
      'cyber-4': { 'Green': 'No known high-risk vulnerabilities detected', 'Yellow': 'Vulnerable applications detected; patches available', 'Red': 'Critical vulnerabilities with known exploits detected' },
      'cyber-5': { 'Green': 'Lateral movement opportunities minimized', 'Yellow': 'High number of local admins increases lateral risk', 'Red': 'Widespread local admin rights enable easy lateral movement' },
      'cyber-6': { 'Green': 'Email security controls properly enforced', 'Yellow': 'Minor email configuration gaps detected', 'Red': 'Significant email exposure (e.g., missing DMARC/SPF)' },
      'cyber-7': { 'Green': 'No suspicious persistence mechanisms detected', 'Yellow': 'Uncommon auto-start entries found; needs review', 'Red': 'Likely persistence mechanisms (e.g., rogue services) detected' },
      'cyber-8': { 'Green': 'Encryption protocols meet modern security standards', 'Yellow': 'Support for legacy encryption (e.g., TLS 1.0) remains', 'Red': 'Weak or broken encryption in use for sensitive data' },
      'cyber-9': { 'Green': 'Administrative access is tightly scoped and reviewed', 'Yellow': 'Administrative group membership is elevated', 'Red': 'Critical: Administrative access sprawl detected' }
    };
    return descriptions[findingId]?.[status] || '';
};

/**
 * Cyber Security (Threat & Exposure) Widget Component
 */
export const CyberSecurityGridWidget = ({ data, config = {}, style = {}, onConfigChange, theme = {} }) => {
  const { title = 'CYBER SECURITY (THREAT & EXPOSURE)', dataOverrides = {} } = config;
  
  const baseFindings = extractCyberSecurityFindings(data, config);

  const getStatusDescription = (finding, status) => {
    return getCyberSecurityStatusDescription(finding.id, status) || finding.description;
  };

  const categories = config?.categories || DEFAULT_CYBER_SECURITY_CATEGORIES;
  const baseFindingsMap = baseFindings.reduce((acc, f) => { acc[f.id] = f; return acc; }, {});

  const handleCardClick = (finding) => {
    if (!onConfigChange) return;
    const statusCycle = ['Green', 'Yellow', 'Red', 'Unknown'];
    const currentStatus = finding.status || 'Unknown';
    const nextStatus = statusCycle[(statusCycle.indexOf(currentStatus) + 1) % statusCycle.length];
    
    let nextScore = 0;
    if (nextStatus === 'Green') nextScore = 100;
    else if (nextStatus === 'Yellow') nextScore = 50;

    onConfigChange({
      ...config,
      dataOverrides: {
        ...dataOverrides,
        [finding.id]: {
          status: nextStatus,
          score: nextScore,
          description: getStatusDescription(finding, nextStatus),
          mode: 'manual'
        }
      }
    });
  };

  const cyberFindings = categories.map(cat => {
    const override = dataOverrides[cat.id];
    const sourceId = (override && override.autoSourceId) ? override.autoSourceId : cat.id;
    const sourceFinding = baseFindingsMap[sourceId];

    const baseFinding = {
      id: cat.id,
      title: sourceFinding ? sourceFinding.title : (cat.label || 'New Item'),
      status: sourceFinding ? sourceFinding.status : 'Unknown',
      score: sourceFinding ? sourceFinding.score : 0,
      description: sourceFinding ? (getStatusDescription(sourceFinding, sourceFinding.status)) : 'Configure this item',
      metrics: sourceFinding ? sourceFinding.metrics : [],
      category: 'Cyber Security'
    };

    let finalFinding = { ...baseFinding, isManual: false };

    if (override && (override.status || override.mode === 'manual')) {
      const statusToScore = { 'Green': 100, 'Yellow': 50, 'Red': 0, 'Unknown': 0 };
      finalFinding = {
        ...baseFinding,
        status: override.status,
        score: override.score !== undefined ? override.score : (statusToScore[override.status] || 0),
        description: override.description || getStatusDescription(baseFinding, override.status),
        isManual: true
      };
    } else if (override && override.mode === 'dataSource') {
      const result = evaluateDataSourceConfig(data, override);
      finalFinding = {
        ...baseFinding,
        status: result.status,
        score: result.score,
        description: result.description,
        metrics: result.metrics.length > 0 ? result.metrics : baseFinding.metrics,
        isDataSource: true
      };
    }

    if (override?.description && (!override.mode || override.mode === 'auto')) {
      finalFinding.description = override.description;
    }

    if (override?.displayName) {
      finalFinding.title = override.displayName;
    } else if (cat.label) {
      finalFinding.title = cat.label;
    }

    return finalFinding;
  });

  const calculateAuditScore = () => {
    const knownFindings = cyberFindings.filter(f => f.status !== 'Unknown');
    if (knownFindings.length === 0) return 0;
    const scores = knownFindings.map(f => f.score);
    const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
    return Number.isInteger(avgScore) ? avgScore : parseFloat(avgScore.toFixed(2));
  };

  const auditScore = calculateAuditScore();

  return (
    <BaseScanGridWidget
      findings={cyberFindings}
      title={title}
      auditScore={auditScore}
      onCardClick={handleCardClick}
      style={style}
      config={config}
      theme={theme}
    />
  );
};
